/**
 * Knowledge Sharing Service
 * Manages knowledge posts with file attachments, likes, and comments
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  arrayUnion,
  arrayRemove,
  increment,
} from "firebase/firestore";
import { FirebaseError } from "firebase/app";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { auth, db, storage } from "@/lib/firebase";

// ============================================================================
// TYPES
// ============================================================================

export interface KnowledgeAttachment {
  id: string;
  name: string;
  type: "image" | "video" | "pdf" | "doc" | "file";
  url: string;
  size: number;
  uploadedAt: Date;
}

export interface KnowledgePost {
  id: string;
  authorId: string;
  authorName: string;
  content: string;
  attachments: KnowledgeAttachment[];
  likes: number;
  likedBy: string[]; // Array of user IDs who liked
  commentCount: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface KnowledgeComment {
  id: string;
  postId: string;
  authorId: string;
  authorName: string;
  content: string;
  createdAt: Date;
}

// ============================================================================
// COLLECTION REFERENCES
// ============================================================================

const postsCollection = collection(db, "knowledge_posts");
const commentsCollection = collection(db, "knowledge_comments");

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const convertTimestamp = (timestamp: Date | Timestamp | undefined): Date => {
  if (timestamp instanceof Date) return timestamp;
  if (timestamp && typeof timestamp === "object" && "toDate" in timestamp) {
    return timestamp.toDate();
  }
  return new Date();
};

const getFileType = (
  fileName: string,
  mimeType: string,
): KnowledgeAttachment["type"] => {
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  if (mimeType.includes("pdf")) return "pdf";
  if (mimeType.includes("word") || mimeType.includes("document")) return "doc";
  return "file";
};

// ============================================================================
// POSTS
// ============================================================================

/**
 * Get all knowledge posts
 */
export async function getAllKnowledgePosts(): Promise<KnowledgePost[]> {
  try {
    const q = query(postsCollection, orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        authorId: data.authorId,
        authorName: data.authorName,
        content: data.content,
        attachments: data.attachments || [],
        likes: data.likes || 0,
        likedBy: data.likedBy || [],
        commentCount: data.commentCount || 0,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
      } as KnowledgePost;
    });
  } catch (error) {
    console.error("Error fetching knowledge posts:", error);
    throw error;
  }
}

/**
 * Get posts by specific user
 */
export async function getPostsByUser(userId: string): Promise<KnowledgePost[]> {
  try {
    const q = query(
      postsCollection,
      where("authorId", "==", userId),
      orderBy("createdAt", "desc"),
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        authorId: data.authorId,
        authorName: data.authorName,
        content: data.content,
        attachments: data.attachments || [],
        likes: data.likes || 0,
        likedBy: data.likedBy || [],
        commentCount: data.commentCount || 0,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
      } as KnowledgePost;
    });
  } catch (error) {
    console.error("Error fetching user posts:", error);
    throw error;
  }
}

/**
 * Get single post by ID
 */
export async function getKnowledgePost(
  postId: string,
): Promise<KnowledgePost | null> {
  try {
    const docRef = doc(postsCollection, postId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        authorId: data.authorId,
        authorName: data.authorName,
        content: data.content,
        attachments: data.attachments || [],
        likes: data.likes || 0,
        likedBy: data.likedBy || [],
        commentCount: data.commentCount || 0,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
      } as KnowledgePost;
    }
    return null;
  } catch (error) {
    console.error("Error fetching post:", error);
    return null;
  }
}

/**
 * Upload attachment to Firebase Storage
 */
export async function uploadKnowledgeAttachment(
  file: File,
  userId: string,
): Promise<KnowledgeAttachment> {
  try {
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const storageRef = ref(storage, `knowledge/${userId}/${fileName}`);

    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);

    return {
      id: `att_${timestamp}`,
      name: file.name,
      type: getFileType(file.name, file.type),
      url,
      size: file.size,
      uploadedAt: new Date(),
    };
  } catch (error) {
    console.error("Error uploading attachment:", error);
    throw error;
  }
}

/**
 * Create new knowledge post
 */
export async function createKnowledgePost(
  content: string,
  authorId: string,
  authorName: string,
  attachments: KnowledgeAttachment[] = [],
): Promise<string> {
  const persistCreate = async (): Promise<string> => {
    const now = Timestamp.now();
    const docRef = await addDoc(postsCollection, {
      authorId,
      authorName,
      content,
      attachments,
      likes: 0,
      likedBy: [],
      commentCount: 0,
      createdAt: now,
      updatedAt: now,
    });

    return docRef.id;
  };

  try {
    return await persistCreate();
  } catch (error) {
    // Claims can be stale right after role/status updates; refresh and retry once.
    if (
      error instanceof FirebaseError &&
      error.code === "permission-denied" &&
      auth.currentUser
    ) {
      await auth.currentUser.getIdToken(true);
      return persistCreate();
    }

    console.error("Error creating post:", error);
    throw error;
  }
}

/**
 * Update knowledge post
 */
export async function updateKnowledgePost(
  postId: string,
  updates: Partial<Pick<KnowledgePost, "content" | "attachments">>,
): Promise<void> {
  try {
    const docRef = doc(postsCollection, postId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("Error updating post:", error);
    throw error;
  }
}

/**
 * Delete knowledge post
 */
export async function deleteKnowledgePost(postId: string): Promise<void> {
  try {
    const docRef = doc(postsCollection, postId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting post:", error);
    throw error;
  }
}

/**
 * Toggle like on a post
 */
export async function togglePostLike(
  postId: string,
  userId: string,
): Promise<void> {
  try {
    const docRef = doc(postsCollection, postId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      const likedBy = data.likedBy || [];
      const isLiked = likedBy.includes(userId);

      await updateDoc(docRef, {
        likes: increment(isLiked ? -1 : 1),
        likedBy: isLiked ? arrayRemove(userId) : arrayUnion(userId),
      });
    }
  } catch (error) {
    console.error("Error toggling like:", error);
    throw error;
  }
}

// ============================================================================
// COMMENTS
// ============================================================================

/**
 * Get comments for a post
 */
export async function getPostComments(
  postId: string,
): Promise<KnowledgeComment[]> {
  try {
    const q = query(
      commentsCollection,
      where("postId", "==", postId),
      orderBy("createdAt", "asc"),
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        postId: data.postId,
        authorId: data.authorId,
        authorName: data.authorName,
        content: data.content,
        createdAt: convertTimestamp(data.createdAt),
      } as KnowledgeComment;
    });
  } catch (error) {
    console.error("Error fetching comments:", error);
    throw error;
  }
}

/**
 * Add comment to a post
 */
export async function addPostComment(
  postId: string,
  authorId: string,
  authorName: string,
  content: string,
): Promise<string> {
  try {
    const now = Timestamp.now();

    // Add comment
    const commentRef = await addDoc(commentsCollection, {
      postId,
      authorId,
      authorName,
      content,
      createdAt: now,
    });

    // Increment comment count
    const postRef = doc(postsCollection, postId);
    await updateDoc(postRef, {
      commentCount: increment(1),
    });

    return commentRef.id;
  } catch (error) {
    console.error("Error adding comment:", error);
    throw error;
  }
}

/**
 * Delete comment
 */
export async function deletePostComment(
  commentId: string,
  postId: string,
): Promise<void> {
  try {
    // Delete comment
    const commentRef = doc(commentsCollection, commentId);
    await deleteDoc(commentRef);

    // Decrement comment count
    const postRef = doc(postsCollection, postId);
    await updateDoc(postRef, {
      commentCount: increment(-1),
    });
  } catch (error) {
    console.error("Error deleting comment:", error);
    throw error;
  }
}
